import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';

import { scriptStepTypes } from '@xmatters/common/constants/scriptSteps';
import {
  buildDependencyMaps,
  iterateTopologically,
  SHORT_CIRCUIT,
} from 'utils/collections';
import { IntegrationOperation } from 'xm-constants/integration';

const { CALLABLE_FLOW_CALLER } = scriptStepTypes;

export const isCallableIntegration = integration =>
  integration?.operation === IntegrationOperation.CALLABLE_TRIGGER;

const getCallerSteps = customSteps =>
  customSteps?.filter(step => step.scriptStepType === CALLABLE_FLOW_CALLER);

export const isCallableTriggerStep = step => step?.callableFlowCaller;

export function getCallerStepForReceiver(receiver, customSteps) {
  if (!receiver) {
    return undefined;
  }
  const callerId = receiver.callableFlowCaller?.id;
  const receiverId = receiver.id;
  const callerSteps = getCallerSteps(customSteps);

  return (
    (callerId && find(callerSteps, step => step.id === callerId)) ||
    // Temporary hack:
    find(callerSteps, step => step.callableFlowReceiver?.id === receiverId)
  );
}

export function getCallerStepForIntegration(integration, customSteps) {
  const integrationStepId = integration?.scriptStep?.id;
  if (!integrationStepId) {
    return null;
  }
  const callerSteps = getCallerSteps(customSteps);
  const receiverStep = find(customSteps, { id: integrationStepId });
  // if step wasn't created (callable trigger wasn't updated)
  if (!receiverStep) {
    return null;
  }
  const callerId = receiverStep?.callableFlowCaller?.id;
  if (!callerId) {
    // Temporary workaround until callableFlowCaller returns
    return find(
      callerSteps,
      step => step.callableFlowReceiver?.id === receiverStep.id,
    );
  }
  return find(callerSteps, { id: callerId });
}

export const isCallableTriggerIntegration = integration =>
  integration?.operation === IntegrationOperation.CALLABLE_TRIGGER;

export const getCallableIntegrations = integrations =>
  integrations.filter(isCallableTriggerIntegration);

export function removeCallableTriggerSteps(scriptSteps, integrations) {
  if (!scriptSteps) {
    return [];
  }
  if (!integrations) {
    return scriptSteps;
  }
  const callableIntegrations = getCallableIntegrations(integrations);
  const callableIntegrationStepIds = new Set(
    callableIntegrations.map(integration => integration.scriptStep?.id),
  );

  return scriptSteps.filter(step => !callableIntegrationStepIds.has(step.id));
}

const isCallableCaller = item => item.scriptStepType === CALLABLE_FLOW_CALLER;
const isCallableReceiver = item => !!item.customStep?.callableFlowCaller;

const isAncestorOrSelf = (blockData, ancestorId) =>
  blockData.id === ancestorId || blockData.allAncestorIds.has(ancestorId);

function getConnectionValidationDependencies({
  blockMetadataMap,
  sourceId,
  targetId,
}) {
  // List of Callable Flow blocks that would be attached on the target side (including target node itself):
  const callableItems = filter(blockMetadataMap, isCallableCaller);
  const downstreamCallableFlowCallers = callableItems.filter(blockData =>
    isAncestorOrSelf(blockData, targetId),
  );
  if (!downstreamCallableFlowCallers.length) {
    // Short circuit - if there are no callables on the target side then the user is necessarily
    // not creating callable cycles or _now_ exceeding the allowedCallablesInFlow.
    return null;
  }
  const sourceBlockMetadata = blockMetadataMap[sourceId];
  const blockIdsToCheck = new Set([
    sourceBlockMetadata.id,
    ...sourceBlockMetadata.allAncestorIds,
    ...filter(
      blockMetadataMap,
      blockData =>
        blockData.id === targetId || blockData.allAncestorIds.has(targetId),
    ).map(item => item.id),
  ]);

  return buildDependencyMaps(blockIdsToCheck, item => {
    const { parentIds } = blockMetadataMap[item];
    if (item === targetId) {
      return parentIds.concat(sourceId);
    }
    return parentIds;
  });
}

function getMaxCallablesSeen({
  maxAncestorCountsById,
  parent,
  dependent,
  isDependantCaller,
}) {
  const previousDependentMax = maxAncestorCountsById[dependent] || 0;
  const itemMaxCount = maxAncestorCountsById[parent] || 0;
  const newMaxCount = Math.max(
    previousDependentMax,
    itemMaxCount + (isDependantCaller ? 1 : 0),
  );
  // eslint-disable-next-line no-param-reassign
  maxAncestorCountsById[dependent] = newMaxCount;
  return newMaxCount;
}

function handleCycleDependencyCheck({
  accessedReceiversById,
  parentMetadata,
  dependentMetadata,
  isDependantCaller,
}) {
  const { customStep, id } = dependentMetadata;
  const parentReceivers = accessedReceiversById[parentMetadata.id];
  const receiverId = customStep?.callableFlowReceiver?.id;

  if (isDependantCaller && parentReceivers.has(receiverId)) {
    return true;
  }
  // eslint-disable-next-line no-param-reassign
  accessedReceiversById[id] = new Set([
    ...parentReceivers,
    ...(accessedReceiversById[id] || []),
  ]);
  return false;
}

/**
 * Callable flows have two validations that are checked when determining if a connection is acceptable:
 * 1. No cycle/recursion is created (non-trampoline only, and this does not count through Continuations)
 * 2. A maximum number of callables can be invoked in a single flow (count resets through Continuations)
 *
 * To check these we perform a sort of dynamic programming approach that involves iterating
 * topologically from the roots out. The logic is:
 * - At the root levels we:
 *    - Set maxAncestorCounts to 1 for Callable Callers
 *    - Set "accessedReceivers" to a new Set, include self if the root is a Callable Receiver
 *      - "accessedReceivers" just means all upstream receivers seen for the current flow
 * - As we resolve dependencies (i.e. edges between blocks), we:
 *    - Skip to next item if it's a Continuation (tested by checking `groupingBlockId`)
 *      - This effectively resets both validations through continuations, as requested in COR-41489 ACs 1.a. and 2.b.
 *    - Update the maxAncestorCounts to the larger value of:
 *      - the current parent's value (+1 if current dependent is a Callable Caller)
 *      - the previous value for the current dependant
 *    - If the new maxAncestorCounts breaches the limit (defined by allowedCallablesInFlow), then
 *      we can short circuit; end iterating immediately. A breach was detected so return an error message.
 *    - Update the accessedReceiversById to merge:
 *      - the previous accessedReceiversById value for the current dependant
 *      - the accessedReceiversById value for the current parent
 *    - If the current dependent is a Callable Caller, and resulting accessedReceiversById includes the related Receiver
 *       then we can short circuit; end iterating immediately. A cycle was detected so return an error message.
 *
 * Assumptions:
 * - groupingBlockId indicates a Continuation (technically not true for Responses, but those won't affect this)
 *
 * Performance assumptions:
 * - If the target-side (in a source->target edge) has no Callable Flow items, then there cannot be newly created issues.
 * - We only need to process nodes connected to either the source or target (so not completely separate flows)
 *
 */
export function validateCallableFlowConnection({
  allowedCallablesInFlow,
  blockMetadataMap,
  sourceId,
  targetId,
}) {
  const dependencies = getConnectionValidationDependencies({
    blockMetadataMap,
    sourceId,
    targetId,
  });
  if (!dependencies) {
    return null;
  }

  // Temp state to help detect max-callables-in-flow breaches:
  const maxAncestorCountsById = {};
  let maxCallablesExceeded = false;
  // Temp state to help detect cycles:
  const accessedReceiversById = {};
  let cycleDetected = false;

  iterateTopologically({
    ...dependencies,
    runOnItem: item => {
      const itemMetadata = blockMetadataMap[item];
      const accessedReceivers = accessedReceiversById[item] || new Set();
      accessedReceiversById[item] = accessedReceivers;
      if (isCallableCaller(itemMetadata)) {
        maxAncestorCountsById[item] = maxAncestorCountsById[item] || 1;
      } else if (itemMetadata.block.groupingBlockId) {
        // COR-41489 AC 2.b: This should be reset after continuations from Incident or Alert activity triggers
        maxAncestorCountsById[item] = 0;
      } else if (isCallableReceiver(itemMetadata)) {
        accessedReceivers.add(itemMetadata.customStep.id);
      }
    },
    runOnDependent: (item, dependent) => {
      const dependentMetadata = blockMetadataMap[dependent];
      // Both cycle-detection and max-count reset through continuations:
      if (dependentMetadata.block.groupingBlockId) {
        return null;
      }
      const isDependantCaller = isCallableCaller(dependentMetadata);
      // Handle max callables:
      const newMaxCount = getMaxCallablesSeen({
        maxAncestorCountsById,
        parent: item,
        dependent,
        isDependantCaller,
      });
      if (newMaxCount > allowedCallablesInFlow) {
        maxCallablesExceeded = true;
        return SHORT_CIRCUIT;
      }

      // Handle cycle detection:
      cycleDetected = handleCycleDependencyCheck({
        accessedReceiversById,
        isDependantCaller,
        parentMetadata: blockMetadataMap[item],
        dependentMetadata,
      });

      return cycleDetected ? SHORT_CIRCUIT : null;
    },
  });

  if (cycleDetected) {
    return { id: 'FLOW_DESIGN_ERROR_CALLABLE_FLOWS_CYCLE_DETECTED' };
  }
  if (maxCallablesExceeded) {
    return {
      id: 'FLOW_DESIGN_ERROR_TOO_MANY_RESULTING_CALLABLE_FLOWS',
      values: {
        allowedCallablesInFlow,
      },
    };
  }

  return null;
}

export const getBlockIdsConnectedToACallable = blockMetadataMap => {
  const callableAndCallerBlocks = new Set(
    filter(
      blockMetadataMap,
      block => isCallableCaller(block) || isCallableReceiver(block),
    ),
  );

  const callableAndCallerBlockIds = new Set(
    [...callableAndCallerBlocks].map(item => item.id),
  );

  // Add IDs of caller and receiver steps
  const blocksIdsConnectedToACallable = new Set([...callableAndCallerBlockIds]);

  // Add IDs of the ancestors of those caller and receiver steps
  callableAndCallerBlocks.forEach(blockData => {
    const { allAncestorIds } = blockData;
    [...allAncestorIds].forEach(ancestorId =>
      blocksIdsConnectedToACallable.add(ancestorId),
    );
  });

  // Add IDs of their children
  const callableAndCallerBlockIdList = [...callableAndCallerBlockIds];
  forEach(blockMetadataMap, blockData => {
    const { id: blockId, allAncestorIds } = blockData;

    if (
      !blocksIdsConnectedToACallable.has(blockId) &&
      !isEmpty(intersection(callableAndCallerBlockIdList, [...allAncestorIds]))
    ) {
      blocksIdsConnectedToACallable.add(blockId);
    }
  });

  return [...blocksIdsConnectedToACallable];
};
